import type Supercluster from 'supercluster';
import { type LatLngBounds, type LatLngBoundsLiteral, type LatLngLiteral, type LatLngTuple } from 'leaflet';
import { isEmptyObject } from 'es-toolkit';
import { bbox, bboxPolygon, booleanEqual, buffer, feature, featureCollection, point } from '@turf/turf';
import {
  convertBoundingBoxDtoToMultiPolygon,
  type BoundingBoxDto,
  type MultiPolygonG2D,
  type SearchGeoPoint,
} from '@ev-tech/search-modules-api';

export function latLngBoundsToBoundingBox(latLngBounds: LatLngBounds): BoundingBoxDto {
  const southWest = latLngBounds.getSouthWest();
  const northEast = latLngBounds.getNorthEast();

  return {
    southWest: { lat: southWest.lat, lng: southWest.lng },
    northEast: { lat: northEast.lat, lng: northEast.lng },
  };
}

export function latLngBoundsToMultiPolygon(latLngBounds: LatLngBounds): MultiPolygonG2D {
  return convertBoundingBoxDtoToMultiPolygon(latLngBoundsToBoundingBox(latLngBounds));
}

export function boundingBoxToLatLngBounds(boundingBox: BoundingBoxDto): LatLngBoundsLiteral {
  return [
    [boundingBox.southWest.lat, boundingBox.southWest.lng],
    [boundingBox.northEast.lat, boundingBox.northEast.lng],
  ];
}

export function multiPolygonG2dToLatLngBounds(multiPolygon: MultiPolygonG2D): LatLngBoundsLiteral {
  const geometry: GeoJSON.MultiPolygon = {
    type: 'MultiPolygon',
    coordinates: multiPolygon.coordinates,
  };
  const [west, south, east, north] = bbox(geometry);
  return geoJsonBboxToLatLngBounds([west, south, east, north]);
}

export function multiPolygonG2dToBoundingBoxDto(multiPolygon: MultiPolygonG2D): BoundingBoxDto {
  const geometry: GeoJSON.MultiPolygon = {
    type: 'MultiPolygon',
    coordinates: multiPolygon.coordinates,
  };
  return geoJsonBboxToBoundingBoxDto(bbox(geometry));
}

export function geoJsonBboxToLatLngBounds([southWestLng, southWestLat, northEastLng, northEastLat]: GeoJSON.BBox): LatLngBoundsLiteral {
  return [
    [southWestLat, southWestLng],
    [northEastLat, northEastLng],
  ];
}

export function boundingBoxToGeoJsonBbox(boundingBox: BoundingBoxDto): GeoJSON.BBox {
  return [boundingBox.southWest.lng, boundingBox.southWest.lat, boundingBox.northEast.lng, boundingBox.northEast.lat];
}

export function combineBoundingBoxes(boundingBoxes: BoundingBoxDto[]): BoundingBoxDto | undefined {
  if (!boundingBoxes.length) return undefined;

  return geoJsonBboxToBoundingBoxDto(
    bbox(featureCollection(boundingBoxes.map((boundingBox) => bboxPolygon(boundingBoxToGeoJsonBbox(boundingBox))))),
  );
}

export function getLatLngBoundsCenter(latLngBounds: LatLngBoundsLiteral): LatLngLiteral {
  const [southWest, northEast] = latLngBounds;

  if (!southWest || !northEast) throw new Error('Invalid latLngBounds');

  return {
    lat: (southWest[0] + northEast[0]) / 2,
    lng: (southWest[1] + northEast[1]) / 2,
  };
}

export function latLngBoundsToGeoJsonBbox(latLngBounds: LatLngBounds): GeoJSON.BBox {
  return [latLngBounds.getWest(), latLngBounds.getSouth(), latLngBounds.getEast(), latLngBounds.getNorth()];
}

export function getVisibleIndices(activeIndex: number, totalItems: number, range = 5) {
  const indices = new Set();

  for (let i = -range; i <= range; i++) {
    const index = (activeIndex + i + totalItems) % totalItems;
    indices.add(index);
  }

  return indices;
}

export function getFlyDurationFromDistance(distance: number): number {
  const scaleFactor = 0.8; // (higher = slower animations for large distances)
  const distanceSensitivity = 0.05;
  const baseDuration = 0.5; // Base duration for very small distances

  const duration = scaleFactor * Math.log(distanceSensitivity * distance + 1) + baseDuration;
  return Math.max(duration, 1); // Ensure minimum of 1 seconds
}

export function latLngEquals(a: LatLngTuple, b: LatLngTuple) {
  return a[0] === b[0] && a[1] === b[1];
}

const EARTH_CIRCUMFERENCE_AT_EQUATOR = 40_075_016.686; // meters, represents the full circumference of the Earth at the equator
const TILE_ZOOM_SCALE_FACTOR = 8; // controls the zoom level calculation, determines tile resolution

export function getPixelsPerMeter(zoom: number, latitude: number) {
  const pixelsPerMeter =
    Math.pow(2, zoom + TILE_ZOOM_SCALE_FACTOR) / (EARTH_CIRCUMFERENCE_AT_EQUATOR * Math.abs(Math.cos((latitude * Math.PI) / 180)));

  return pixelsPerMeter.toFixed(4);
}

export function areSetsEqual(a: Set<unknown>, b: Set<unknown>) {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/**
 * Returns true if _not_ all children share the same coordinates.
 * @param children Points or other clusters within a cluster
 * @returns boolean
 */
export function checkForDistinctChildren(children: Supercluster.PointFeature<SearchGeoPoint>[]): boolean {
  return children.map((child) => point(child.geometry.coordinates)).some((feature, _i, arr) => !!arr[0] && !booleanEqual(feature, arr[0]));
}

/**
 * Convert an array of points from the BFF geopoints endpoint into a GeoJSON FeatureCollection object.
 * @param results - An array of GeoPoints from the BFF geopoints endpoint
 * @returns An array of GeoJSON Feature objects
 */
export function computeGeoJsonFeatures(results: SearchGeoPoint[]): GeoJSON.Feature<GeoJSON.Point, SearchGeoPoint>[] {
  const features = [];

  // For-of loop instead of map for performance reasons
  for (const geoResult of results) {
    if (isEmptyObject(geoResult.displayLocation)) continue;

    features.push(
      feature(
        {
          type: 'Point',
          coordinates: [geoResult.displayLocation.lng, geoResult.displayLocation.lat],
        },
        geoResult,
      ),
    );
  }

  return features;
}

/**
 * Convert an array of points from the BFF geopoints endpoint into a Leaflet LatLngBoundsLiteral object.
 * @param results - An array of GeoPoints from the BFF geopoints endpoint
 * @returns A Leaflet LatLngBoundsLiteral object
 */
export function searchGeoPointsToLatLngBounds(results: SearchGeoPoint[]): LatLngBoundsLiteral {
  const features = computeGeoJsonFeatures(results);

  const boundingBox = bbox({ type: 'FeatureCollection', features });

  return geoJsonBboxToLatLngBounds(boundingBox);
}

function geoJsonBboxToBoundingBoxDto(geojsonBbox: GeoJSON.BBox): BoundingBoxDto {
  return {
    southWest: { lng: geojsonBbox[0], lat: geojsonBbox[1] },
    northEast: { lng: geojsonBbox[2], lat: geojsonBbox[3] },
  };
}

/**
 * Expands a rectangular bounding box by a radius in kilometers.
 */
export function expandBoundingBox(boundingBox: BoundingBoxDto, radiusKm: number): BoundingBoxDto {
  if (radiusKm <= 0) return boundingBox;

  const expandedBbox = buffer(bboxPolygon(boundingBoxToGeoJsonBbox(boundingBox)), radiusKm, { units: 'kilometers' });

  if (!expandedBbox) return boundingBox;

  return geoJsonBboxToBoundingBoxDto(bbox(expandedBbox));
}
